import pubSubGlobal from "./pubSubGlobal";
import { FOCUS_ON_OPEN, FOCUS_SETTINGS } from "../model/Constants";
import { isAndroid, isIOS } from "./Misc";
import { log } from "../chatframe/Utils";

export default class FocusMgr {
    $app: any;

    isPopup: boolean;

    focusTimeout: any;

    inputElement: any;

    constructor(appInstance: any) {
        this.$app = appInstance;
        this.isPopup = this.$app.getSDKInstance().getChatParams().chatType === "POPUP";
        this.focusTimeout = -1;
    }

    setInputElement(inputElement: any): void {
        this.inputElement = inputElement;
    }

    /**
     * returns true or false depends on what type of device and where the current focus
     * and if the activeElement is body then look for element with attribute data-focus-id
     * to check whether last clicked element is within the transcript area
     * @param {CIApp} appInstance
     * @return {boolean}
     */
    shouldExecuteFocusSetting() {
        const isMobile = isAndroid(null) || isIOS();
        const isMinimized = this.$app.getIsMinimized();
        const isFocusInInput = this.inputElement.hasFocus();
        const transcriptArea = ".transcript-area";
        const isModalVisible = this.$app.getStateMgr().state.isModalVisible;
        let isFocusWithinTranscript = document.activeElement.closest(transcriptArea);

        // activeElement can shift to body for ex. if clicked element is disabled programmatically
        if (!isFocusWithinTranscript && document.activeElement === document.body) {
            const vaLinkElement = document.querySelector("[data-focus-id='vaLink']");

            if (vaLinkElement) {
                isFocusWithinTranscript = vaLinkElement.closest(transcriptArea);
                vaLinkElement.removeAttribute("data-focus-id");
            }
        }
        return (isMobile && !isMinimized && !isFocusInInput && !isModalVisible) || isFocusWithinTranscript;
    }

    /**
     * If activeElement is not on a descendant of the <transcript-area> element, do nothing
     * If focusOnAgentMessage is 'onInput': Assign focus to the input area
     * If focusOnAgentMessage is 'onInteractiveElement': If a link is inside the new agent message, assign focus to the first interactive element in the new agent message
     * If no link is inside the new agent message, do nothing
     * If focusOnAgentMessage is 'onNewAgentMessage': If standard agent message: Apply tabindex="-1" to the new .agent-bubble .bubble Assign focus to the new .agent-bubble .bubble
     * If rich media: Apply tabindex="-1" to the new .nuan-widget-container
     * Assign focus to the new .nuan-widget-container
     * @param {HTMLElement} element
     * @return {Boolean}
     */
    handleFocusSetting(element: any, focusSettingMode: any): boolean {
        if (this.shouldExecuteFocusSetting()) {
            switch (focusSettingMode) {
                case FOCUS_SETTINGS.ON_INPUT:
                    this.inputElement.focus();
                    break;

                case FOCUS_SETTINGS.ON_INTERACTIVE_ELEMENT:
                    this.focusLinkTag(element);
                    break;

                case FOCUS_SETTINGS.ON_NEW_AGENT_MESSAGE:
                    if (element) {
                        element.setAttribute("tabindex", "-1");
                        element.focus();
                    }
                    return false;

                default:
                    log(`not a valid focus setting${focusSettingMode}`);
            }
        }
        return true;
    }

    handleFocusWhenChatLaunched(element: any, focusSettingMode: any): boolean {
        switch (focusSettingMode) {
            case FOCUS_ON_OPEN.ON_INPUT:
                // Autofocus if not in survey mode.
                if (!this.$app.getStateMgr().state.survey.visible && !this.$app.getIsMinimized() && !this.isPopup) {
                    this.inputElement.focus();
                }

                break;

            case FOCUS_ON_OPEN.ON_OPENER:
                if (!this.isPopup) {
                    element.focus();
                }
                return false;

            default:
                log(`focus setting when chat launched: ${focusSettingMode}`);
        }
        return true;
    }

    /**
     * Set focus to the link tag found within the given customer or agent bubble element
     * @param {HTMLElement} bubbleElement
     */
    focusLinkTag(bubbleElement: any): void {
        if (bubbleElement) {
            const link = bubbleElement.querySelector("a");

            if (link) {
                link.focus();
            }
        }
    }

    forceInputFocus() {
        if (this.inputElement) {
            this.inputElement.focus();
        }
    }

    /**
     * Set Focus to input if focus is not in transcript area
     */
    setFocusIfFocusNotInTranscript() {
        if (!this.shouldExecuteFocusSetting()) {
            this.inputElement.focus();
        }
    }

    whenFrameRestored() {
        /**
         * when user minimize and restore in the same page, need to put the focus back input from restore button
         * Delay to make sure chat visibility is painted
         */
        this.clearRestoreFocusTimeout();
        this.focusTimeout = setTimeout(() => {
            this.forceInputFocus();
        }, 2000);
    }

    clearRestoreFocusTimeout() {
        if (this.focusTimeout !== -1) {
            clearTimeout(this.focusTimeout);
            this.focusTimeout = -1;
        }
    }
}
